import { useEffect, useRef, useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";

export default function SocialMediaEmbeds() {
  const { t } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [scriptsLoaded, setScriptsLoaded] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "100px" }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible || scriptsLoaded) return;

    const loadScripts = () => {
      try {
        // Suppress console errors from third-party scripts
        const originalConsoleError = console.error;
        console.error = (...args: any[]) => {
          // Filter out TikTok SDK errors
          const errorString = args.join(' ');
          if (errorString.includes('tiktok') || errorString.includes('webmssdk')) {
            return;
          }
          originalConsoleError.apply(console, args);
        };

        const tiktokScript = document.createElement("script");
        tiktokScript.src = "https://www.tiktok.com/embed.js";
        tiktokScript.async = true;
        tiktokScript.onerror = () => {
          console.warn('TikTok embed script failed to load');
        };
        document.head.appendChild(tiktokScript);

        const instagramScript = document.createElement("script");
        instagramScript.src = "//www.instagram.com/embed.js";
        instagramScript.async = true;
        instagramScript.onerror = () => {
          console.warn('Instagram embed script failed to load');
        };
        document.head.appendChild(instagramScript);

        // Add accessibility titles to iframes after they load
        const addIframeTitles = () => {
          const instagramIframe = document.querySelector('iframe.instagram-media');
          if (instagramIframe && !instagramIframe.hasAttribute('title')) {
            instagramIframe.setAttribute('title', 'Instagram embed for MagicDoor');
          }

          const tiktokIframe = document.querySelector('iframe[src*="tiktok.com/embed"]');
          if (tiktokIframe && !tiktokIframe.hasAttribute('title')) {
            tiktokIframe.setAttribute('title', 'TikTok embed for @magicdoor1999');
          }
        };

        // Check for iframes periodically until they're loaded
        const checkInterval = setInterval(() => {
          addIframeTitles();
          const instagramIframe = document.querySelector('iframe.instagram-media');
          const tiktokIframe = document.querySelector('iframe[src*="tiktok.com/embed"]');
          if (instagramIframe && tiktokIframe) {
            clearInterval(checkInterval);
          }
        }, 500);

        // Clear interval after 10 seconds max
        setTimeout(() => {
          clearInterval(checkInterval);
          console.error = originalConsoleError;
        }, 10000);
      } catch (error) {
        console.warn('Error loading social media embeds:', error);
      }
    };

    loadScripts();
    setScriptsLoaded(true);

    return () => {
      const tiktokScriptToRemove = document.querySelector(
        'script[src="https://www.tiktok.com/embed.js"]'
      );
      if (tiktokScriptToRemove) {
        document.head.removeChild(tiktokScriptToRemove);
      }
      const instagramScriptToRemove = document.querySelector(
        'script[src="//www.instagram.com/embed.js"]'
      );
      if (instagramScriptToRemove) {
        document.head.removeChild(instagramScriptToRemove);
      }
    };
  }, [isVisible, scriptsLoaded]);

  return (
    <section
      ref={sectionRef}
      className="min-h-[90svh] md:min-h-[100svh] section-snap py-8 md:py-12 text-center flex flex-col justify-center px-4"
    >
      <div className="grid gap-16 lg:grid-cols-2 mb-8 max-w-7xl mx-auto">
        {/* Instagram Feed */}
        <div className="space-y-6" data-aos="fade-up">
          <h3 className="text-2xl font-semibold">{t("home.follow.instagram")}</h3>
          <div className="flex justify-center">
            {isVisible ? (
              <div className="w-full max-w-[500px] border-2 border-primary/20 rounded-2xl p-3 shadow-lg bg-background/50 backdrop-blur-sm">
                <blockquote
                  className="instagram-media"
                  data-instgrm-permalink="https://www.instagram.com/magicdoor.ro/"
                  data-instgrm-version="14"
                  style={{
                    background: "#FFF",
                    border: "0",
                    borderRadius: "12px",
                    boxShadow: "none",
                    margin: "0",
                    maxWidth: "100%",
                    minWidth: "320px",
                    padding: "0",
                    width: "100%",
                    height: "450px",
                  }}
                >
                  <div style={{ padding: "16px" }}>
                    <a
                      href="https://www.instagram.com/magicdoor.ro/"
                      style={{
                        background: "#FFFFFF",
                        lineHeight: "0",
                        padding: "0 0",
                        textAlign: "center",
                        textDecoration: "none",
                        width: "100%",
                      }}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                        }}
                      >
                        <div
                          style={{
                            backgroundColor: "#F4F4F4",
                            borderRadius: "50%",
                            flexGrow: "0",
                            height: "40px",
                            marginRight: "14px",
                            width: "40px",
                          }}
                        ></div>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            flexGrow: "1",
                            justifyContent: "center",
                          }}
                        >
                          <div
                            style={{
                              backgroundColor: "#F4F4F4",
                              borderRadius: "4px",
                              flexGrow: "0",
                              height: "14px",
                              marginBottom: "6px",
                              width: "100px",
                            }}
                          ></div>
                          <div
                            style={{
                              backgroundColor: "#F4F4F4",
                              borderRadius: "4px",
                              flexGrow: "0",
                              height: "14px",
                              width: "60px",
                            }}
                          ></div>
                        </div>
                      </div>
                      <div style={{ padding: "19% 0" }}></div>
                      <div
                        style={{
                          display: "block",
                          height: "50px",
                          margin: "0 auto 12px",
                          width: "50px",
                        }}
                      >
                        <div className="story-link">{t("home.instagram.link")}</div>
                      </div>
                    </a>
                  </div>
                </blockquote>
              </div>
            ) : (
              <div className="w-full max-w-[500px] h-[450px] border-2 border-primary/20 rounded-2xl animate-pulse bg-muted" />
            )}
          </div>
        </div>

        {/* TikTok Feed */}
        <div className="space-y-6" data-aos="fade-up" data-aos-delay="200">
          <h3 className="text-2xl font-semibold">{t("home.follow.tiktok")}</h3>
          <div className="flex justify-center">
            {isVisible ? (
              <div className="w-full max-w-[500px] border-2 border-primary/20 rounded-2xl p-3 shadow-lg bg-background/50 backdrop-blur-sm">
                <blockquote
                  className="tiktok-embed"
                  cite="https://www.tiktok.com/@magicdoor1999"
                  data-unique-id="magicdoor1999"
                  data-embed-type="creator"
                  style={{
                    maxWidth: "100%",
                    minWidth: "320px",
                    height: "450px",
                    borderRadius: "12px",
                    overflow: "hidden",
                    backgroundColor: "transparent",
                  }}
                >
                  <section>
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      href="https://www.tiktok.com/@magicdoor1999?refer=creator_embed"
                      className="story-link"
                    >
                      @magicdoor1999
                    </a>
                  </section>
                </blockquote>
              </div>
            ) : (
              <div className="w-full max-w-[500px] h-[450px] border-2 border-primary/20 rounded-2xl animate-pulse bg-muted" />
            )}
          </div>
        </div>
      </div>

      {/* Under Development Message */}
      <div className="border-t pt-6" data-aos="fade-up" data-aos-delay="400">
        <p className="text-lg text-muted-foreground">{t("home.development")}</p>
      </div>
    </section>
  );
}
